import { BASE_URL } from '../constants';
import { getAuthHeader } from '../utils';

const ENDPOINT = `${BASE_URL}insurance/letter-of-guarantee/`;

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.append(k, v);
  });
  return qs.toString();
}

async function ensureOk(res, defaultMsg) {
  if (!res.ok) {
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    const payload = data.error || data.detail || data.status;
    if (typeof payload === 'string' && payload) {
      throw new Error(payload);
    }
    // Errores por campo de DRF: { "holder": ["..."] } (llegan en el body
    // sin clave `detail`).
    const source = payload && typeof payload === 'object' ? payload : data;
    const fields = Object.entries(source || {})
      .map(([field, messages]) => [
        field,
        [].concat(messages).filter(message => typeof message === 'string'),
      ])
      .filter(([, messages]) => messages.length)
      .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
      .join(' · ');
    throw new Error(fields || defaultMsg);
  }
  return res.json();
}

const JSON_HEADERS = () => ({
  'Content-Type': 'application/json',
  ...getAuthHeader(),
});

/**
 * Helper: armar payload JSON. Si el caller ya subió los archivos a S3
 * (vía useS3Upload), debe pasar `documents_data: [{key, file_name,
 * file_type, category}]` y se serializa tal cual. Si NO, se acepta el
 * shape legacy con FormData (compatible con el backend actual).
 */
function buildJsonPayload(fields) {
  const out = {};
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    out[k] = v;
  });
  return JSON.stringify(out);
}

export const letterOfGuaranteeService = {
  list: (params = {}) =>
    fetch(`${ENDPOINT}?${buildQuery(params)}`, {
      headers: getAuthHeader(),
    }).then(r => ensureOk(r, 'Error al listar solicitudes')),

  retrieve: id =>
    fetch(`${ENDPOINT}${id}/`, { headers: getAuthHeader() }).then(r =>
      ensureOk(r, 'Error al cargar la solicitud')
    ),

  /**
   * Crear solicitud.
   *
   * Modo S3 (recomendado): el caller pasa `documents_data: [{key,
   * file_name, file_type, category}]` y los demás campos del wizard
   * (subsidiary, patient, medical_center, etc.) a nivel raíz. El
   * backend descarga cada key de S3 y crea el LetterOfGuaranteeDocument.
   *
   * Modo legacy: el caller pasa un `FormData` con archivos multipart
   * (documents[] + document_categories[]) — todavía soportado.
   */
  create: payload => {
    if (payload instanceof FormData) {
      return fetch(ENDPOINT, {
        method: 'POST',
        headers: getAuthHeader(),
        body: payload,
      }).then(r => ensureOk(r, 'Error al crear la solicitud'));
    }
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: JSON_HEADERS(),
      body: buildJsonPayload(payload),
    }).then(r => ensureOk(r, 'Error al crear la solicitud'));
  },

  update: (id, payload) => {
    if (payload instanceof FormData) {
      return fetch(`${ENDPOINT}${id}/`, {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: payload,
      }).then(r => ensureOk(r, 'Error al actualizar la solicitud'));
    }
    return fetch(`${ENDPOINT}${id}/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: buildJsonPayload(payload),
    }).then(r => ensureOk(r, 'Error al actualizar la solicitud'));
  },

  /**
   * Aprobar solicitud. observation + approvedAmount son obligatorios.
   * El PDF firmado puede venir:
   *   - approvalKey: key de S3 (modo recomendado)
   *   - approvalFile: File nativo (modo legacy multipart)
   */
  approve: (
    id,
    {
      observation = '',
      approvalFile = null,
      approvalKey = null,
      approvalFileName = null,
      approvedAmount = null,
    } = {}
  ) => {
    if (!observation || !observation.trim()) {
      throw new Error('Debe escribir el motivo de la aprobación.');
    }
    if (approvedAmount == null || Number(approvedAmount) <= 0) {
      throw new Error('Debe indicar el monto aprobado en USD.');
    }

    if (approvalKey) {
      return fetch(`${ENDPOINT}${id}/approve/`, {
        method: 'PATCH',
        headers: JSON_HEADERS(),
        body: JSON.stringify({
          observation,
          approved_amount: String(approvedAmount),
          approval_document_key: approvalKey,
          approval_document_name: approvalFileName || `approval_${id}.pdf`,
        }),
      }).then(r => ensureOk(r, 'Error al aprobar la solicitud'));
    }

    if (!approvalFile) {
      throw new Error('Debe adjuntar el PDF firmado por el seguro para aprobar.');
    }
    const fd = new FormData();
    fd.append('observation', observation);
    fd.append('approval_document', approvalFile);
    fd.append('approved_amount', String(approvedAmount));
    return fetch(`${ENDPOINT}${id}/approve/`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: fd,
    }).then(r => ensureOk(r, 'Error al aprobar la solicitud'));
  },

  /**
   * Rechazar solicitud. observation obligatorio + PDF firmado.
   * Mismos dos modos que approve.
   */
  reject: (
    id,
    { observation = '', approvalFile = null, approvalKey = null, approvalFileName = null } = {}
  ) => {
    if (!observation || !observation.trim()) {
      throw new Error('Debe escribir el motivo del rechazo.');
    }

    if (approvalKey) {
      return fetch(`${ENDPOINT}${id}/reject/`, {
        method: 'PATCH',
        headers: JSON_HEADERS(),
        body: JSON.stringify({
          observation,
          approval_document_key: approvalKey,
          approval_document_name: approvalFileName || `rejection_${id}.pdf`,
        }),
      }).then(r => ensureOk(r, 'Error al rechazar la solicitud'));
    }

    if (!approvalFile) {
      throw new Error('Debe adjuntar el PDF firmado por el seguro para rechazar.');
    }
    const fd = new FormData();
    fd.append('observation', observation);
    fd.append('approval_document', approvalFile);
    return fetch(`${ENDPOINT}${id}/reject/`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: fd,
    }).then(r => ensureOk(r, 'Error al rechazar la solicitud'));
  },

  /**
   * Cambiar el status a uno INTERNO. Solo MASTER.
   *
   * @param {string|number} id
   * @param {object} params
   * @param {'pendiente_documento'|'asesoria_medica'|'en_negociacion'} params.status
   * @param {string} params.observation motivo obligatorio
   * @param {string[]} [params.missingCategories] solo si status === pendiente_documento
   */
  setInternalStatus: (id, { status, observation, missingCategories = [] } = {}) => {
    if (!status) throw new Error('Debe indicar el status destino.');
    if (!observation || !observation.trim()) {
      throw new Error('Debe indicar el motivo del cambio.');
    }
    const body = { status, observation };
    if (
      status === 'pendiente_documento' &&
      Array.isArray(missingCategories) &&
      missingCategories.length > 0
    ) {
      body.missing_categories = missingCategories;
    }
    return fetch(`${ENDPOINT}${id}/set-internal-status/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: JSON.stringify(body),
    }).then(r => ensureOk(r, 'Error al cambiar el status interno'));
  },

  /**
   * Marcar la solicitud como entregada. Roles permitidos: ADMIN,
   * CALL_CENTER, MEDICAL_CENTER. Solo si status === aprobado.
   */
  markDelivered: id =>
    fetch(`${ENDPOINT}${id}/mark-delivered/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: JSON.stringify({}),
    }).then(r => ensureOk(r, 'Error al marcar como entregada')),

  /**
   * Agregar un documento (soporte) a una solicitud existente.
   * Mismos dos modos que approve:
   *   - Modo S3: {key, file_name, file_type, category}
   *   - Modo legacy: {file, category}
   */
  addDocument: (id, opts) => {
    if (typeof opts === 'string' || opts instanceof File) {
      // Firma legacy: addDocument(id, file, category).
      const file = opts;
      const category = arguments[2];
      if (!file) throw new Error('Debe adjuntar un archivo PDF.');
      if (!category) throw new Error('Debe indicar la categoría del documento.');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      return fetch(`${ENDPOINT}${id}/add_document/`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd,
      }).then(r => ensureOk(r, 'Error al agregar el documento'));
    }
    const { key, file_name, file_type, category } = opts || {};
    if (!key) throw new Error('Debe proporcionar la key de S3 del archivo.');
    if (!category) throw new Error('Debe indicar la categoría del documento.');
    return fetch(`${ENDPOINT}${id}/add_document/`, {
      method: 'POST',
      headers: JSON_HEADERS(),
      body: JSON.stringify({
        key,
        file_name: file_name || `document_${id}.pdf`,
        file_type: file_type || 'application/pdf',
        category,
      }),
    }).then(r => ensureOk(r, 'Error al agregar el documento'));
  },

  /**
   * Historial / trazabilidad de una solicitud.
   * Combina cambios en la solicitud + documentos asociados.
   * Solo accesible para MASTER/ADMIN (el backend valida el rol).
   */
  history: async id => {
    const res = await fetch(`${ENDPOINT}${id}/history/`, {
      headers: getAuthHeader(),
    });
    // Leemos como texto primero para soportar respuestas no-JSON (404 HTML
    // cuando el endpoint aún no está desplegado en el backend).
    const text = await res.text();
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      // No era JSON. Si fue OK, esto es inesperado; si no, lo reportamos.
      if (!res.ok) {
        throw new Error(
          `Endpoint no disponible (HTTP ${res.status}). Verifica que el backend tenga el merge con el commit del historial.`
        );
      }
      throw new Error('Respuesta inesperada del servidor.');
    }
    if (!res.ok) {
      throw new Error(
        payload?.error || payload?.detail || `Error al cargar el historial (HTTP ${res.status})`
      );
    }
    return payload;
  },

  /**
   * Lista las notas humanas (bitácora) de una solicitud, en orden
   * descendente por fecha. Accesible para roles internos.
   */
  listNotes: async id => {
    const res = await fetch(`${ENDPOINT}${id}/notes/`, {
      headers: getAuthHeader(),
    });
    const text = await res.text();
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      if (!res.ok) {
        throw new Error(`Endpoint de notas no disponible (HTTP ${res.status}).`);
      }
      throw new Error('Respuesta inesperada del servidor.');
    }
    if (!res.ok) {
      throw new Error(
        payload?.error || payload?.detail || `Error al cargar la bitácora (HTTP ${res.status})`
      );
    }
    return payload;
  },

  /**
   * Crea una nueva nota en la bitácora de una solicitud. La nota es
   * inmutable: una vez guardada no se puede editar ni eliminar.
   */
  addNote: async (id, body) => {
    const res = await fetch(`${ENDPOINT}${id}/notes/`, {
      method: 'POST',
      headers: { ...JSON_HEADERS(), ...getAuthHeader() },
      body: JSON.stringify({ body }),
    });
    return ensureOk(res, 'Error al agregar la nota');
  },
};
